<template>
    <form class="form" novalidate="novalidate" id="st_login_form">
        <div class="form-group">
            <label> {{ $t("AUTH.INPUT.EMAIL") }} *</label>
            <div id="email-input-group" label="" label-for="email-input-group">
                <input class="form-control form-control-solid h-auto py-4 px-6 rounded-sm" type="text" name="email"
                    ref="email" v-model="form.email" />
            </div>
        </div>
        <div class="form-group last">
            <div class="d-flex justify-content-between mt-n5">
                <label class="pt-5">{{ $t("AUTH.INPUT.PASSWORD") }} *</label>
                <router-link v-if="!isPopUpEnabled" :to="{ path: '/auth/forgot-password' }"
                    class="text-primary font-size-sm font-weight-bold text-hover-primary pt-5">
                    {{ $t("AUTH.LOGIN.FORGOT_PASSWORD_LABEL") }}
                </router-link>
                <a v-if="isPopUpEnabled" href="/auth/forgot-password"
                    class="text-primary font-size-sm font-weight-bold text-hover-primary pt-5">{{
                        $t("AUTH.LOGIN.FORGOT_PASSWORD_LABEL") }}</a>
            </div>
            <div id="password-input-group" label="" label-for="password-input-group">
                <input class="form-control form-control-solid h-auto py-4 px-6 rounded-sm" type="password"
                    name="password" ref="password" autocomplete="off" v-model="form.password" />
            </div>
        </div>
        <div class="pb-lg-0 mb-5">
            <button id="st_submit_button" ref="st_submit_button" class="btn btn-primary font-size-h4 w-100">
                {{ $t("AUTH.LOGIN.BUTTON") }}
            </button>
        </div>
        <div v-if="isRegisterEnabled" class="pb-lg-0 mb-5">
            <router-link :to="{ path: '/auth/signup' }">
                <button type="button" id="st_cancel_button" class="btn btn-outline-primary font-size-h4 w-100">
                    {{ $t("AUTH.LOGIN.NO_ACCOUNT") }}
                </button>
            </router-link>
        </div>
        <p v-if="isRegisterEnabled" class="mb-0">
            <span>{{ $t("AUTH.LOGIN.ROEID_INFO_TEXT_FIRST") }}
                <a href="https://www.roeid.ro/">ROeID</a>
            </span>
            <span>{{ $t("AUTH.LOGIN.ROEID_INFO_TEXT_SECOND") }}</span>
        </p>
        <i18n v-if="isRegisterEnabled" path="AUTH.LOGIN.ROEID_INFO_DESCRIPTION" tag="p" class="auth-help-text">
            <template #roeid><a href="https://www.roeid.ro/">ROeID</a></template>
            <template #adr><a href="https://adr.gov.ro/">{{ $t("AUTH.LOGIN.ROEID_INFO_ADR_LINK_TEXT") }}</a></template>
            <template #ec><a href="https://ec.europa.eu/digital-building-blocks/sites/display/EIDCOMMUNITY/Romania+-+ROeID">{{ $t("AUTH.LOGIN.ROEID_INFO_EC_LINK_TEXT") }}</a></template>
        </i18n>
    </form>
</template>

<script>
import formValidation from "@/assets/plugins/formvalidation/dist/es6/core/Core";

// FormValidation plugins
import Trigger from "@/assets/plugins/formvalidation/dist/es6/plugins/Trigger";
import Bootstrap from "@/assets/plugins/formvalidation/dist/es6/plugins/Bootstrap";
import SubmitButton from "@/assets/plugins/formvalidation/dist/es6/plugins/SubmitButton";

import KTUtil from "@/assets/js/components/util";

import { mapActions, mapGetters } from "vuex";

import { LOGIN, LOGOUT, GET_PROFILE, UPDATE_PROFILE } from "@/modules/auth/auth-store";
import { getPcuelAccountId, destroyPcuelAccountId, getPcuelOrganizationId } from '@/shared/services/pcuel-account.service';
import { fetchPcuelApplicant } from '@/shared/services/pcuel-applicant.service';
import PermissionChecker from '@/security/permission-checker';

export default {
    props: {
        isRegisterEnabled: {
            type: Boolean,
            default: false,
        },
        isPopUpEnabled: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            form: {
                email: "",
                password: "",
            },
        };
    },
    computed: {
        ...mapGetters(["auth/currentUser", "auth/accountDetails"]),
    },
    methods: {
        ...mapActions({
            login: `auth/${LOGIN}`,
            logout: `auth/${LOGOUT}`,
            getProfile: `auth/${GET_PROFILE}`,
            updateProfile: `auth/${UPDATE_PROFILE}`,
            getLastPcuelSync: 'auth/getLastPcuelSync',
        }),
        showConfirmationModal(error) {
            const email = this?.form?.email;
            this.$alert({
                hasConfirmation: true,
                type: "warning",
                text: this.$t(`ERRORS.${error.message}`),
                confirmCallback: () => {
                    this.$router.push({
                        name: "changePassword",
                        params: { email },
                    });
                },
            });
        },
        getMessage(error) {
            if (!error?.data) return this.$t(`ERRORS.${error.message}`);
            if (error.data?.remaining_attempts) {
                return this.$t(`ERRORS.${error.message}`, [
                    error.data.remaining_attempts,
                ]);
            }
            return this.$t(`ERRORS.${error.message}`);
        },
        hasAccessToReturnUrl(returnUrl) {
            const { resolved } = this.$router.resolve({ path: `/${returnUrl}`.replace(/^\/+/, '/') });
            const meta = resolved.meta;
            if (!meta?.permission && !meta?.permissions) {
                return true;
            }
            const permissionChecker = new PermissionChecker(this['auth/accountDetails']);
            if (meta.permission) {
                return permissionChecker.match(meta.permission);
            }
            return meta.permissions.some((permission) => permissionChecker.match(permission));
        },
    },
    async created() {
        const accountId = getPcuelAccountId();
        const organizationId = getPcuelOrganizationId();
        if (accountId && organizationId) {
            let applicant = null;
            try {
                applicant = await fetchPcuelApplicant({ accountId, organizationId });
            } catch (e) {
                applicant = null;
            }
            if (applicant && applicant.userEmail && !this.form.email) {
                this.form.email = applicant.userEmail;
            }
        }
    },
    mounted() {
        if (!this.isPopUpEnabled) {
            this.logout();
        }

        const signin_form = KTUtil.getById("st_login_form");

        this.fv = formValidation(signin_form, {
            fields: {
                email: {
                    validators: {
                        notEmpty: {
                            message: this.$t("AUTH.VALIDATION.REQUIRED"),
                        },
                        emailAddress: {
                            message: this.$t("AUTH.VALIDATION.EMAIL"),
                        },
                    },
                },
                password: {
                    validators: {
                        notEmpty: {
                            message: this.$t("AUTH.VALIDATION.REQUIRED"),
                        },
                    },
                },
            },
            plugins: {
                trigger: new Trigger(),
                submitButton: new SubmitButton(),
                bootstrap: new Bootstrap(),
            },
        });

        this.fv.on("core.form.valid", () => {
            const email = this.form.email;
            const password = this.form.password;

            // set spinner to submit button
            const submitButton = this.$refs["st_submit_button"];
            submitButton.classList.add(
                "spinner",
                "spinner-light",
                "spinner-right"
            );

            this.login({
                email: email,
                password: password,
            })
                .then((data) => {
                    const { isEmailConfirmed, isPhoneConfirmed } = data;
                    
                    if (!isEmailConfirmed) {
                        this.$router.push({ name: "verifyEmail" });
                    } else if (!isPhoneConfirmed) {
                        this.$router.push({ name: "verifySMS" });
                    } else {
                        this.getProfile().then(async () => {
                            const returnUrl = this.$route.query.returnUrl;
                            if (this.isPopUpEnabled) {
                                this.$emit('hideModal', { route: "home" });
                            } else if (returnUrl && this.hasAccessToReturnUrl(returnUrl)) {
                                // Return the user to the deep link they came from
                                // (e.g. a PCUEL "Detalii" link to /application/:id).
                                this.$router.push({ path: `/${returnUrl}`.replace(/^\/+/, '/') });
                            } else {
                                // Either there's no returnUrl, or the logged-in user doesn't
                                // have access to it (e.g. a different user than the one the
                                // returnUrl was generated for) - drop it so it can't be
                                // replayed on a later sign-in and cause a redirect loop.
                                this.$router.replace({ name: "dashboard" });
                            }

                            if (this['auth/currentUser']?.pcuel_account_id) {
                                await this.getLastPcuelSync(this['auth/currentUser'].pcuel_account_id);
                            }

                            const accountId = getPcuelAccountId();
                            if (accountId) {
                                const alreadySynced = this['auth/currentUser']?.pcuel_account_id;
                                if (alreadySynced) {
                                    const isDifferentAccount = alreadySynced !== accountId;
                                    destroyPcuelAccountId();
                                    this.$nextTick(() => {
                                        this.$alert({
                                            type: 'warning',
                                            text: this.$t(isDifferentAccount ? 'PCUEL.DIFFERENT_ACCOUNT_TEXT' : 'PCUEL.ALREADY_SYNCED_TEXT'),
                                            confirmButtonText: this.$t('GENERAL.BUTTON.CONTINUE'),
                                            showConfirmButton: true,
                                            showCloseButton: true,
                                            callback: () => {
                                                this.$router.push({
                                                    name: 'applicationNew',
                                                    query: { appTypeKey: 'AUTORIZATIE_CONSTRUIRE' },
                                                });
                                            },
                                        });
                                    });
                                } else {
                                    this.updateProfile({}).then((profile) => {
                                        if (profile?.pcuel_link_conflict) {
                                            destroyPcuelAccountId();
                                            this.$nextTick(() => {
                                                this.$alert({
                                                    type: 'error',
                                                    text: this.$t('PCUEL.CONFLICT_TEXT'),
                                                });
                                            });
                                            return;
                                        }
                                        this.$nextTick(() => {
                                            this.$alert({
                                                type: 'warning',
                                                text: this.$t('PCUEL.INFO_MODAL_TEXT'),
                                                confirmButtonText: this.$t('GENERAL.BUTTON.CONTINUE'),
                                                showConfirmButton: true,
                                                showCloseButton: true,
                                                callback: () => {
                                                    this.$router.push({
                                                        name: 'applicationNew',
                                                        query: { appTypeKey: 'AUTORIZATIE_CONSTRUIRE' },
                                                    });
                                                },
                                            });
                                        });
                                    });
                                }
                            }
                        });
                    }
                })
                .catch((error) => {
                    if (error.message === "AUTH_FAILED_PASSWORD_EXPIRED") {
                        this.showConfirmationModal(error);
                        return;
                    }
                    const message = this.getMessage(error);
                    this.$notify({
                        type: "error",
                        message,
                    });
                });

            submitButton.classList.remove(
                "spinner",
                "spinner-light",
                "spinner-right"
            );
        });
    },
};
</script>
